import { getFirebaseMessaging, VAPID_KEY } from '../config/firebase';
import { getToken, onMessage, Messaging } from 'firebase/messaging';
import api from './api';
import orgService from './orgService';

class FCMService {
  private messaging: Messaging | null = null;
  private tokenRefreshCallback: ((token: string) => void) | null = null;
  private tokenCheckInterval: NodeJS.Timeout | null = null;

  /**
   * Check if browser supports FCM
   */
  isSupported(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return (
      'Notification' in window &&
      'serviceWorker' in navigator &&
      'PushManager' in window
    );
  }

  /**
   * Check if current user is admin
   */
  isAdmin(): boolean {
    const role = orgService.getRole();
    return role === 'admin';
  }

  /**
   * Check if FCM should be initialized (admin only)
   */
  shouldInitialize(): boolean {
    return this.isSupported() && this.isAdmin();
  }

  /**
   * Request notification permission from user.
   * Wrapped with a timeout so the promise cannot hang indefinitely (e.g. in Microsoft Edge
   * when the permission prompt does not appear or "quieter messaging" is enabled).
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      throw new Error('Browser does not support notifications');
    }

    const PERMISSION_TIMEOUT_MS = 10000; // 10 seconds

    const permissionWithTimeout = new Promise<NotificationPermission>((resolve) => {
      const timeoutId = setTimeout(() => {
        console.warn(
          'Notification.requestPermission timed out (e.g. prompt not shown in this browser). ' +
          'Proceeding without push notifications.'
        );
        resolve(Notification.permission);
      }, PERMISSION_TIMEOUT_MS);

      Notification.requestPermission()
        .then((permission) => {
          clearTimeout(timeoutId);
          resolve(permission);
        })
        .catch((error) => {
          clearTimeout(timeoutId);
          console.error('Error requesting notification permission:', error);
          resolve(Notification.permission);
        });
    });

    return permissionWithTimeout;
  }

  /**
   * Initialize FCM and get messaging instance
   */
  initialize(): Messaging | null {
    if (!this.isSupported()) {
      console.warn('FCM is not supported in this browser');
      return null;
    }

    try {
      this.messaging = getFirebaseMessaging();
      return this.messaging;
    } catch (error) {
      console.error('Error initializing FCM:', error);
      return null;
    }
  }

  /**
   * Wait for service worker to be ready
   */
  private async waitForServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (!('serviceWorker' in navigator)) {
      return null;
    }

    try {
      // Check if service worker is already registered
      const registration = await navigator.serviceWorker.ready;
      return registration;
    } catch (error) {
      console.warn('Service worker not ready, attempting to register...');
      try {
        // Try to register if not already registered
        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        // Wait for it to be ready
        await navigator.serviceWorker.ready;
        return registration;
      } catch (regError) {
        console.error('Failed to register service worker:', regError);
        return null;
      }
    }
  }

  /**
   * Get FCM token for the current device
   */
  async getToken(): Promise<string | null> {
    if (!this.messaging) {
      this.messaging = this.initialize();
      if (!this.messaging) {
        return null;
      }
    }

    // Wait for service worker to be ready
    const swRegistration = await this.waitForServiceWorker();
    if (!swRegistration) {
      console.warn('Service worker not available, cannot get FCM token');
      return null;
    }

    try {
      const currentToken = await getToken(this.messaging, {
        vapidKey: VAPID_KEY,
      });

      if (currentToken) {
        // Store token in localStorage for persistence
        localStorage.setItem('fcm_token', currentToken);
        return currentToken;
      } else {
        console.warn('No registration token available. Request permission to generate one.');
        return null;
      }
    } catch (error) {
      console.error('An error occurred while retrieving token:', error);
      return null;
    }
  }

  /**
   * Send FCM token to backend
   */
  async sendTokenToBackend(token: string): Promise<void> {
    try {
      console.log('Sending FCM token to backend API: PUT /api/org/user');
      const response = await api.put('/api/org/user', {
        fcm_token: token,
      });
      console.log('FCM token sent to backend successfully', response.status);
    } catch (error: any) {
      console.error('Error sending FCM token to backend:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
      });
      throw error;
    }
  }

  /**
   * Initialize FCM, request permission, get token, and send to backend
   */
  async initializeAndRegister(): Promise<string | null> {
    try {
      console.log('Starting FCM initialization...');
      
      // Check if user is admin
      if (!this.isAdmin()) {
        console.log('FCM initialization skipped: User is not an admin');
        return null;
      }
      
      // Check if already initialized
      if (!this.messaging) {
        this.messaging = this.initialize();
        if (!this.messaging) {
          console.error('Failed to initialize FCM messaging');
          return null;
        }
      }

      // Check permission
      const permission = Notification.permission;
      console.log('Current notification permission:', permission);
      
      if (permission === 'default') {
        const newPermission = await this.requestPermission();
        console.log('Permission request result:', newPermission);
        if (newPermission !== 'granted') {
          console.warn('Notification permission denied');
          return null;
        }
      } else if (permission !== 'granted') {
        console.warn('Notification permission not granted');
        return null;
      }

      // Check stored token BEFORE getting new token
      const storedToken = localStorage.getItem('fcm_token');
      console.log('Stored token before getToken:', storedToken ? 'exists' : 'none');

      // Get token
      console.log('Requesting FCM token...');
      const token = await this.getToken();
      if (!token) {
        console.error('Failed to get FCM token');
        return null;
      }

      console.log('FCM token obtained:', token.substring(0, 20) + '...');

      // Always send token to backend on login/initialization
      // Check if token has changed to avoid unnecessary API calls
      if (!storedToken || storedToken !== token) {
        console.log('Token is new or changed, sending to backend...');
        try {
          await this.sendTokenToBackend(token);
          console.log('Token successfully sent to backend');
        } catch (error) {
          console.error('Failed to send token to backend:', error);
          // Don't throw - we still want to return the token even if backend call fails
        }
      } else {
        console.log('Token unchanged, but sending to backend to ensure registration...');
        // Even if token is the same, send it to ensure backend has it
        try {
          await this.sendTokenToBackend(token);
          console.log('Token successfully sent to backend (re-registration)');
        } catch (error) {
          console.error('Failed to send token to backend (re-registration):', error);
        }
      }

      // Set up token refresh listener
      this.setupTokenRefresh();

      return token;
    } catch (error) {
      console.error('Error initializing FCM:', error);
      return null;
    }
  }

  /**
   * Set up listener for token refresh
   * Firebase v9+ doesn't have onTokenRefresh, so we check periodically
   */
  private setupTokenRefresh(): void {
    if (!this.messaging) {
      return;
    }

    // Clear any existing interval
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
    }

    // Check for token changes every 5 minutes
    this.tokenCheckInterval = setInterval(async () => {
      try {
        const currentToken = await this.getToken();
        const storedToken = localStorage.getItem('fcm_token');
        
        if (currentToken && currentToken !== storedToken) {
          console.log('FCM token refreshed, sending to backend...');
          try {
            await this.sendTokenToBackend(currentToken);
          } catch (error) {
            console.error('Failed to send refreshed token to backend:', error);
          }
        }
      } catch (error) {
        console.error('Error checking token refresh:', error);
      }
    }, 5 * 60 * 1000); // Check every 5 minutes
  }

  /**
   * Set up listener for foreground messages
   */
  onMessage(callback: (payload: any) => void): void {
    if (!this.messaging) {
      console.warn('FCM messaging not initialized');
      return;
    }

    try {
      onMessage(this.messaging, (payload) => {
        console.log('Message received in foreground:', payload);
        callback(payload);
      });
    } catch (error) {
      console.error('Error setting up message listener:', error);
    }
  }

  /**
   * Clean up FCM service
   */
  cleanup(): void {
    // Clear stored token
    localStorage.removeItem('fcm_token');
    
    // Clear token check interval
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }
    
    // Note: Firebase v9+ doesn't require explicit cleanup for messaging
    this.messaging = null;
  }

  /**
   * Get stored FCM token from localStorage
   */
  getStoredToken(): string | null {
    return localStorage.getItem('fcm_token');
  }
}

const fcmService = new FCMService();
export default fcmService;
